import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";

const money = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function TeamRecordsPage() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [hours, setHours] = useState("");
  const [rate, setRate] = useState("");
  const [description, setDescription] = useState("Paid training");
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState(null);

  // Development-type picker state
  const [devEmpId, setDevEmpId] = useState("");
  const [devType, setDevType] = useState("foundation");
  const [devSaving, setDevSaving] = useState(false);

  useEffect(() => {
    api.get("/users")
      .then((res) => setEmployees(res.data.filter((u) => u.role === "employee")))
      .catch(() => setEmployees([]));
  }, []);

  const loadRecords = useCallback(async (eid) => {
    if (!eid) { setRecords(null); return; }
    try {
      const res = await api.get(`/admin/training-records/${eid}`);
      setRecords(res.data);
    } catch { setRecords(null); }
  }, []);

  useEffect(() => { loadRecords(employeeId); }, [employeeId, loadRecords]);

  // Reflect the chosen employee's stored development type in the picker.
  useEffect(() => {
    if (!devEmpId) { setDevType("foundation"); return; }
    const emp = employees.find((u) => u._id === devEmpId);
    setDevType(emp?.development_type || "foundation");
  }, [devEmpId, employees]);

  const saveDevType = async () => {
    if (!devEmpId) { toast.error("Pick a team member first"); return; }
    setDevSaving(true);
    try {
      await api.patch(`/users/${devEmpId}`, { development_type: devType });
      setEmployees((prev) => prev.map((u) => (u._id === devEmpId ? { ...u, development_type: devType } : u)));
      toast.success("Development type updated");
    } catch {
      toast.error("Could not update development type");
    } finally {
      setDevSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId || !hours) { toast.error("Pick an employee and enter hours"); return; }
    setSaving(true);
    try {
      await api.post("/admin/training-records", {
        employee_id: employeeId,
        date,
        hours: parseFloat(hours),
        rate: rate ? parseFloat(rate) : null,
        description,
        source: "manual entry",
      });
      toast.success("Training record added");
      setHours(""); setRate("");
      loadRecords(employeeId);
    } catch (err) {
      toast.error("Could not save record");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="team-records-page">
      <div>
        <h1 className="text-2xl font-semibold text-stone-800">Team Records — Paid Training</h1>
        <p className="text-sm text-stone-500">Log paid-training hours for any team member. This feeds their "GT's Investment in You" story.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Add a training record</CardTitle>
          <CardDescription>Pay is computed as hours × rate (defaults to the employee's hourly rate).</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="tr-emp">Employee</Label>
              <select id="tr-emp" data-testid="tr-employee-select" value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="w-full mt-1 border border-stone-300 rounded-md px-3 py-2 text-sm bg-white">
                <option value="">Select an employee…</option>
                {employees.map((u) => (
                  <option key={u._id} value={u._id}>{u.display_name || u.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="tr-date">Date</Label>
                <Input id="tr-date" type="date" data-testid="tr-date-input" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="tr-hours">Hours</Label>
                <Input id="tr-hours" type="number" step="0.01" data-testid="tr-hours-input" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="e.g. 4" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="tr-rate">Rate ($/hr, optional)</Label>
                <Input id="tr-rate" type="number" step="0.01" data-testid="tr-rate-input" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="employee default" />
              </div>
              <div>
                <Label htmlFor="tr-desc">Description</Label>
                <Input id="tr-desc" data-testid="tr-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
            </div>
            <Button type="submit" disabled={saving} data-testid="tr-submit-button">
              {saving ? "Saving…" : "Add record"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card data-testid="dev-type-card">
        <CardHeader>
          <CardTitle>Development type</CardTitle>
          <CardDescription>
            Sets how each person's growth story reads. <strong>Foundation</strong> = learning the craft from
            the start; <strong>Transition</strong> = joined already skilled and made GT home; <strong>Custom</strong> =
            no development framing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div className="sm:col-span-1">
              <Label htmlFor="dev-emp">Team member</Label>
              <select id="dev-emp" data-testid="dev-employee-select" value={devEmpId}
                onChange={(e) => setDevEmpId(e.target.value)}
                className="w-full mt-1 border border-stone-300 rounded-md px-3 py-2 text-sm bg-white">
                <option value="">Select a team member…</option>
                {employees.map((u) => (
                  <option key={u._id} value={u._id}>{u.display_name || u.name}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-1">
              <Label htmlFor="dev-type">Type</Label>
              <select id="dev-type" data-testid="dev-type-select" value={devType}
                onChange={(e) => setDevType(e.target.value)}
                disabled={!devEmpId}
                className="w-full mt-1 border border-stone-300 rounded-md px-3 py-2 text-sm bg-white disabled:bg-stone-100">
                <option value="foundation">Foundation</option>
                <option value="transition">Transition</option>
                <option value="custom">Custom (no framing)</option>
              </select>
            </div>
            <div className="sm:col-span-1">
              <Button onClick={saveDevType} disabled={!devEmpId || devSaving} data-testid="dev-type-save-button" className="w-full">
                {devSaving ? "Saving…" : "Save type"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {records && (
        <Card data-testid="tr-records-list">
          <CardHeader>
            <CardTitle>Recorded training</CardTitle>
            <CardDescription>
              {records.total_hours} hours · ${money(records.total_pay)} paid to date
            </CardDescription>
          </CardHeader>
          <CardContent>
            {records.records.length === 0 ? (
              <EmptyState testId="team-records-empty" line="No records yet" sub="Logged hours and adjustments for this person will appear here." compact />
            ) : (
              <div className="divide-y divide-stone-100">
                {records.records.map((r, i) => (
                  <div key={i} className="flex justify-between py-2 text-sm">
                    <span className="text-stone-700">{r.date || "—"} · {r.description || "Training"}</span>
                    <span className="text-stone-800 font-medium">{r.hours} hrs · ${money(r.pay)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
